import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';
import AuthNavButtons from './AuthNavButtons';
import { SERVICE_CATALOG } from '../config/services';
import { 
  Heart,
  Menu,
  X, 
  Search,
  MessageCircle,
  Calendar,
  Settings,
  LogOut,
  User,
  ChevronDown,
  Star,
  Bell,
  BarChart3,
  Award,
  Briefcase,
  Newspaper,
  HelpCircle
} from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
    setIsProfileOpen(false);
  };

  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const servicesListRef = useRef(null);
  const servicesMenuRef = useRef(null);

  const services = SERVICE_CATALOG;

  const closeServicesDropdown = useCallback(() => {
    setShowServicesDropdown(false);
  }, []);

  useEffect(() => {
    closeServicesDropdown();
    setMobileServicesOpen(false);
    setIsMenuOpen(false);
    setIsProfileOpen(false);
  }, [location.pathname, closeServicesDropdown]);

  useEffect(() => {
    if (!showServicesDropdown) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') closeServicesDropdown();
    };

    const handleClickOutside = (event) => {
      if (servicesMenuRef.current && !servicesMenuRef.current.contains(event.target)) {
        closeServicesDropdown();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showServicesDropdown, closeServicesDropdown]);

  useEffect(() => {
    if (!showServicesDropdown || !servicesListRef.current) return undefined;

    const list = servicesListRef.current;
    const onWheel = (event) => {
      if (list.scrollHeight <= list.clientHeight) return;

      const atTop = list.scrollTop <= 0;
      const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;

      if ((event.deltaY < 0 && !atTop) || (event.deltaY > 0 && !atBottom)) {
        event.preventDefault();
      }
    };

    list.addEventListener('wheel', onWheel, { passive: false });
    return () => list.removeEventListener('wheel', onWheel);
  }, [showServicesDropdown]);

  const isActive = (path) => {
    if (path === '/search') {
      return location.pathname === '/search';
    }
    return location.pathname === path;
  };

  const getMobileNavItems = () => {
    const items = getNavItems();
    if (!user) return [];
    if (user.role === 'owner') {
      const paths = ['/owner-dashboard', '/search', '/owner-messages', '/owner-bookings'];
      return paths.map((path) => items.find((item) => item.path === path)).filter(Boolean);
    }
    if (user.role === 'caregiver') {
      const paths = ['/caregiver-dashboard', '/caregiver-bookings', '/caregiver-messages', '/caregiver-expenses'];
      return paths.map((path) => items.find((item) => item.path === path)).filter(Boolean);
    }
    return items.slice(0, 4);
  };

  const getHomeUrl = () => {
    if (!user) return '/';
    
    switch (user.role) {
      case 'admin':
        return '/admin-dashboard';
      case 'caregiver':
        return '/caregiver-profile';
      case 'owner':
        return '/owner-profile';
      default:
        return '/owner-profile';
    }
  };

  // Navigation items based on user login status and role
  const getNavItems = () => {
    if (!user) {
      // Guest users - show marketing/discovery content
      return [
        { path: '/search', label: 'Find Care', icon: Search },
        { 
          path: '/services', 
          label: 'Services', 
          icon: Briefcase,
          hasDropdown: true,
          dropdownItems: services
        },
        { path: '/blog', label: 'Blog', icon: Newspaper },
        { path: '/become-caregiver', label: 'Caregivers', icon: Heart },
        { path: '/how-it-works', label: 'How It Works', icon: HelpCircle },
        { path: '/testimonials', label: 'Testimonials', icon: Star }
      ];
    } else if (user.role === 'owner') {
      // Pet owners - focus on finding care and managing pets
      return [
        { path: '/owner-profile', label: 'Profile', icon: User },
        { path: '/owner-dashboard', label: 'Dashboard', icon: Calendar },
        { path: '/search', label: 'Find Care', icon: Search },
        { path: '/owner-pets', label: 'My Pets', icon: Heart },
        { path: '/saved-caregivers', label: 'Saved', icon: Heart },
        { path: '/owner-bookings', label: 'My Bookings', icon: Calendar },
        { path: '/owner-messages', label: 'Messages', icon: MessageCircle },
        { path: '/owner-offline-jobs', label: 'Offline Jobs', icon: Settings, isOptional: true }
      ];
    } else if (user.role === 'caregiver') {
      // Caregivers - focus on managing services and bookings
      return [
        { path: '/caregiver-profile', label: 'Profile', icon: User },
        { path: '/caregiver-dashboard', label: 'Dashboard', icon: Calendar },
        { path: '/caregiver-services', label: 'My Services', icon: Settings },
        { path: '/caregiver-bookings', label: 'Bookings', icon: Bell },
        { path: '/caregiver-messages', label: 'Messages', icon: MessageCircle },
        { path: '/caregiver-expenses', label: 'Expenses', icon: Settings },
        { path: '/caregiver-offline-jobs', label: 'Offline Jobs', icon: Settings, isOptional: true }
      ];
    } else if (user.role === 'admin') {
      // Admin - focus on platform management
      return [
        { path: '/admin-dashboard', label: 'Dashboard', icon: Calendar },
        { path: '/admin-reporting', label: 'Reporting', icon: BarChart3 },
        { path: '/admin-marketing', label: 'Marketing', icon: Star },
        { path: '/admin-badges', label: 'Badges', icon: Award },
        { path: '/admin-settings', label: 'Settings', icon: Settings }
      ];
    }
    return [];
  };

  const navItems = getNavItems();

  return (
    <>
      {/* Desktop Navigation */}
      <nav className={`pf-nav-v2 pf-glass pf-glass--nav fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'pf-nav-v2--scrolled' : ''
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center h-[4.25rem] gap-3 lg:gap-4">
            {/* Logo Section */}
            <div className="flex items-center min-w-0 overflow-hidden">
              <Logo
                to={getHomeUrl()}
                size={38}
                onClick={() => setIsMenuOpen(false)}
                title={user ? `Go to ${user.role} dashboard` : 'Go to homepage'}
                className="transform transition-transform group-hover:scale-[1.02] max-w-full"
              />
            </div>

            {/* Desktop Menu - Center (wide screens only) */}
            <div className="hidden xl:flex items-center justify-center gap-0.5 min-w-0">
              {navItems.map((item) => {
                if (item.hasDropdown) {
                  return (
                    <div
                      key={item.path}
                      ref={servicesMenuRef}
                      className={`pf-services-menu${showServicesDropdown ? ' pf-services-menu--open' : ''}`}
                    >
                      <button
                        type="button"
                        aria-expanded={showServicesDropdown}
                        aria-haspopup="true"
                        onClick={() => setShowServicesDropdown((open) => !open)}
                        className="pf-services-menu__trigger nav-link px-2.5 py-2 font-medium text-sm flex items-center gap-1 whitespace-nowrap"
                      >
                        {item.label}
                        <ChevronDown className="pf-services-menu__chevron w-4 h-4 transition-transform" />
                      </button>

                      <div className="pf-services-menu__panel absolute left-0 w-80 z-50">
                        <div className="pf-glass pf-glass--panel rounded-xl overflow-hidden flex flex-col">
                          <div className="px-4 pt-4 pb-2 shrink-0 border-b border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900">Our Pet Care Services</h3>
                          </div>
                          <div
                            ref={servicesListRef}
                            className="pf-services-dropdown__list overflow-y-auto max-h-64 overscroll-contain px-2 py-2"
                          >
                            <div className="grid grid-cols-1 gap-1">
                              {services.map((service) => {
                                const ServiceIcon = service.icon;
                                return (
                                <Link
                                  key={service.slug}
                                  to={`/search?service=${service.slug}`}
                                  className="flex items-start gap-2 p-2 rounded-lg hover:bg-[var(--pf-accent-soft)] transition-colors"
                                  onClick={closeServicesDropdown}
                                >
                                  <span className="w-8 h-8 rounded-lg bg-[var(--pf-accent-soft)] flex items-center justify-center shrink-0">
                                    <ServiceIcon className="w-4 h-4 text-[var(--pf-accent)]" />
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-gray-900 text-sm">{service.label}</h4>
                                    <p className="text-xs text-gray-600 line-clamp-2">{service.description}</p>
                                    <p className="text-xs font-semibold text-[var(--pf-accent)]">{service.price}</p>
                                  </div>
                                </Link>
                              );})}
                            </div>
                          </div>
                          <div className="px-4 py-3 border-t border-gray-200 shrink-0 bg-white">
                            <Link
                              to="/search"
                              className="block text-center pf-btn-primary py-2 px-4 text-sm font-medium"
                              onClick={closeServicesDropdown}
                            >
                              View All Services
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-link px-2.5 py-2 font-medium text-sm whitespace-nowrap ${
                      isActive(item.path) ? 'nav-link-active' : ''
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {/* Auth + mobile menu */}
            <div className="flex items-center justify-end gap-2 shrink-0">
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="w-8 h-8 rounded-full cursor-pointer"
                  >
                    <img
                      src={user.avatar || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&h=150&q=80'}
                      alt={user.name || 'User Avatar'}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  </button>

                  {/* Dropdown Menu */}
                  {isProfileOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                      {/* User Info Header */}
                      <div className="px-4 py-3 border-b border-gray-200 bg-[var(--pf-accent-soft)]">
                        <div className="flex items-center gap-3">
                          <img
                            src={user.avatar || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&h=150&q=80'}
                            alt={user.name || 'User'}
                            className="w-12 h-12 rounded-full object-cover border-2 border-[var(--pf-mint-deep)]"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-gray-900 truncate">{user.name || 'User'}</p>
                            <p className="text-sm font-medium text-[var(--pf-accent)] capitalize">{user.role || 'Member'}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Menu Items */}
                      <Link
                        to={user.role === 'admin' ? '/admin-settings' : user.role === 'caregiver' ? '/caregiver-settings' : '/owner-settings'}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        Settings
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left transition-colors"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="hidden xl:block">
                  <AuthNavButtons
                    hideSignIn={location.pathname === '/login'}
                    hideJoin={location.pathname === '/signup'}
                  />
                </div>
              )}

              {/* Mobile / tablet menu button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="xl:hidden p-2 text-gray-600 hover:text-[var(--pf-accent)] focus:outline-none shrink-0"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-40 xl:hidden pf-animate-overlay-in"
            onClick={() => setIsMenuOpen(false)}
          />
          
          {/* Menu */}
          <div className="fixed inset-y-0 right-0 w-full max-w-sm pf-glass pf-glass--sheet z-50 xl:hidden pf-animate-drawer-in">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="p-5 border-b border-[var(--pf-border)]">
                {user ? (
                  <div className="flex items-center gap-4">
                    <img
                      src={user.avatar || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&h=150&q=80'}
                      alt={user.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-[var(--pf-mint-deep)] shadow-md"
                    />
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-lg">{user.name || 'User'}</h3>
                      <p className="text-sm font-medium text-[var(--pf-accent)] capitalize">{user.role || 'Member'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center space-y-3 pf-mobile-nav-guest rounded-xl p-4 -mx-2">
                    <p className="text-sm font-medium">Join our pet care community</p>
                    <AuthNavButtons
                      layout="stack"
                      onNavigate={() => setIsMenuOpen(false)}
                      hideSignIn={location.pathname === '/login'}
                      hideJoin={location.pathname === '/signup'}
                    />
                  </div>
                )}
              </div>

              {/* Navigation Items */}
              <div className="flex-1 overflow-y-auto py-4">
                <div className="space-y-1 px-3">
                  {navItems.map((item) => {
                    const Icon = item.icon;

                    if (item.hasDropdown) {
                      return (
                        <div key={item.path}>
                          <button
                            type="button"
                            onClick={() => setMobileServicesOpen((open) => !open)}
                            className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-colors ${
                              mobileServicesOpen || location.pathname === '/search'
                                ? 'text-[var(--pf-accent)] bg-[var(--pf-accent-soft)]'
                                : 'text-gray-600 hover:text-[var(--pf-accent)] hover:bg-[var(--pf-accent-soft)]'
                            }`}
                          >
                            <span className="flex items-center gap-3">
                              <Icon className="w-5 h-5" />
                              {item.label}
                            </span>
                            <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {mobileServicesOpen && (
                            <div className="mt-1 ml-2 pl-2 border-l-2 border-[var(--pf-border)] space-y-1">
                              {services.map((service) => {
                                const ServiceIcon = service.icon;
                                return (
                                  <Link
                                    key={service.slug}
                                    to={`/search?service=${service.slug}`}
                                    onClick={() => {
                                      setIsMenuOpen(false);
                                      setMobileServicesOpen(false);
                                    }}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-600 hover:text-[var(--pf-accent)] hover:bg-[var(--pf-accent-soft)]"
                                  >
                                    <ServiceIcon className="w-4 h-4 shrink-0" />
                                    {service.label}
                                  </Link>
                                );
                              })}
                              <Link
                                to="/search"
                                onClick={() => {
                                  setIsMenuOpen(false);
                                  setMobileServicesOpen(false);
                                }}
                                className="block px-3 py-2 text-sm font-medium text-[var(--pf-accent)]"
                              >
                                View all services
                              </Link>
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive(item.path)
                            ? 'text-[var(--pf-accent)] bg-[var(--pf-accent-soft)]'
                            : 'text-gray-600 hover:text-[var(--pf-accent)] hover:bg-[var(--pf-accent-soft)]'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Footer */}
              {user && (
                <div className="p-4 border-t border-gray-200">
                  <Link
                    to={user.role === 'admin' ? '/admin-settings' : user.role === 'caregiver' ? '/caregiver-settings' : '/owner-settings'}
                    className="flex items-center gap-3 px-4 py-3 text-gray-600 hover:text-[var(--pf-accent)] hover:bg-[var(--pf-accent-soft)] rounded-lg"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Settings className="w-5 h-5" />
                    Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <LogOut className="w-5 h-5" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Mobile Bottom Navigation */}
      {user && (
        <div className="pf-mobile-bottom-nav fixed bottom-0 left-0 right-0 bg-[var(--pf-surface)] border-t border-[var(--pf-border)] lg:hidden z-40 backdrop-blur-md">
          <div className="flex justify-around items-center h-16">
            {getMobileNavItems().map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex flex-col items-center gap-1 px-3 ${
                    isActive(item.path)
                      ? 'text-[var(--pf-accent)]'
                      : 'text-gray-600'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;